import { ColorBlue600 } from "@nxt-costco-com/forge-design-tokens";
function getMidpoint(coords) {
  if (!coords.length) return null;
  let x = 0, y = 0, z = 0;
  coords.forEach(([lat, lon]) => {
    const latRad = lat * Math.PI / 180;
    const lonRad = lon * Math.PI / 180;
    x += Math.cos(latRad) * Math.cos(lonRad);
    y += Math.cos(latRad) * Math.sin(lonRad);
    z += Math.sin(latRad);
  });
  const total = coords.length;
  x /= total;
  y /= total;
  z /= total;
  const lonMid = Math.atan2(y, x) * (180 / Math.PI);
  const hyp = Math.sqrt(x * x + y * y);
  const latMid = Math.atan2(z, hyp) * (180 / Math.PI);
  return [latMid, lonMid];
}
function computeMapData(warehouses) {
  const markers = warehouses.slice(0, 10).map(({ address }) => {
    return [address.latitude, address.longitude];
  });
  if (markers.length === 1) {
    return {
      markers,
      midpoint: markers[0]
      // Use the single warehouse's coordinates as midpoint
    };
  }
  return {
    markers,
    midpoint: getMidpoint(markers.slice(1, 10))
  };
}
function computeMapDataWithWarehouses(warehouses) {
  if (!warehouses.length) {
    return { warehouses: [], midpoint: null };
  }
  const limitedWarehouses = warehouses;
  const simplifiedWarehouses = limitedWarehouses.map((warehouse) => {
    var _a, _b;
    return {
      warehouseId: warehouse.warehouseId,
      name: ((_b = (_a = warehouse.name) == null ? void 0 : _a[0]) == null ? void 0 : _b.value) || "",
      coordinates: [
        warehouse.address.latitude,
        warehouse.address.longitude
      ],
      city: warehouse.address.city,
      state: warehouse.address.territory,
      // territory is the state abbreviation
      distance: warehouse.distance,
      distanceUnit: warehouse.distanceUnit
    };
  });
  if (simplifiedWarehouses.length === 1) {
    return {
      warehouses: simplifiedWarehouses,
      midpoint: simplifiedWarehouses[0].coordinates
    };
  }
  const coordinates = simplifiedWarehouses.map((w) => w.coordinates);
  return {
    warehouses: simplifiedWarehouses,
    midpoint: getMidpoint(coordinates)
  };
}
function generateWarehouseHref(warehouseName, city, state, warehouseId) {
  warehouseName = String(warehouseName);
  city = String(city);
  state = String(state);
  warehouseId = String(warehouseId);
  if (warehouseName.toLowerCase() === city.toLowerCase()) {
    warehouseName = warehouseName.toLowerCase() + "-" + state.toLowerCase();
  } else {
    warehouseName = warehouseName.toLowerCase() + "-" + city.toLowerCase() + "-" + state.toLowerCase();
  }
  return "/warehouse-locations/".concat(warehouseName.replace(/[^\w\d]/gi, "-").replace(/-{2,}/g, "-")).concat("-" + warehouseId + ".html");
}
function selectPin(pindex, map) {
  const Microsoft = window.Microsoft;
  const pushpin = map.entities.get(pindex - 1);
  if (!pushpin) {
    console.warn(`Pushpin at index ${pindex} does not exist.`);
    return;
  }
  const data = { target: pushpin };
  try {
    Microsoft == null ? void 0 : Microsoft.Maps.Events.invoke(pushpin, "click", data);
  } catch (error) {
    console.error("Failed to invoke click event on pushpin:", error);
  }
}
const PRERENDERED_INFOBOX_TEMPLATES = {
  // Basic warehouse template for locator view
  warehouse: `<div class="Infobox ">
        <div class="Infobox-body">
            <div class="Infobox-header">
                <div class="Infobox-title-content">
                    <span>
                        <a href="{{href}}" data-automation-id="mapPopUpSelectedWarehouseName">
                            {{locationName}}
                        </a>
                        &nbsp;({{distance}}&nbsp;{{measure}})
                    </span>
                </div>
                <button type="button" class="Infobox-close" aria-label="close" onclick="window.BingMapNamespace?.closeInfobox?.()" data-automation-id="mapPopUpCloseIconLink">
                    <span class="co-close" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="15px" height="15px" role="presentation" focusable="false" aria-hidden="true" color="#777">
                            <path fill-rule="evenodd" clip-rule="evenodd" d="M21.799 19.932 13.866 12l7.932-7.932c.258-.258.27-.663.028-.905l-.989-.99c-.242-.24-.648-.229-.906.03L12 10.133 4.068 2.202c-.259-.258-.664-.27-.907-.029l-.988.99c-.243.242-.23.648.027.905L10.133 12l-7.93 7.932c-.259.258-.27.663-.029.905l.989.988c.241.244.648.23.904-.026L12 13.866l7.932 7.932c.258.258.663.27.905.027l.988-.988c.243-.242.232-.648-.026-.905Z" fill="currentColor"></path>
                        </svg>
                    </span>
                </button>
            </div>
        </div>
    </div>`
};
const PRERENDERED_PIN_TEMPLATES = {
  // Warehouse pin template
  warehouse: `<svg width="21.37" height="30" xmlns="http://www.w3.org/2000/svg"><path id="warehouse-pin" fill="${ColorBlue600}" d="M9,0A8.9,8.9,0,0,0,1.9,14.2l5.7,9.9v0.2h0a1.6,1.6,0,0,0,2.5.1h0.1l0.2-.4,5.6-9.7A8.9,8.9,0,0,0,9,0ZM8.9,13.4A4.4,4.4,0,1,1,13.3,9,4.4,4.4,0,0,1,8.9,13.4Z"/></svg>`,
  // Locator pin template with number and weight placeholders
  locator: `<svg aria-hidden="true" focusable="false" width="21.37" height="30" xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg"><title>Pin</title><g><path d="m10.69,0a10.69,10.69 0 0 0 -8.56,17.08l6.85,11.92a1.93,1.93 0 0 0 0.13,0.22l0,0l0,0a1.92,1.92 0 0 0 3,0.12l0,0l0.07,-0.12a1.93,1.93 0 0 0 0.25,-0.44l6.72,-11.63a10.69,10.69 0 0 0 -8.46,-17.15z" fill="${ColorBlue600}"/><text xml:space="preserve" text-anchor="middle" font-weight="{{weight}}" font-family="Helvetica Neue, Helvetica, Arial, Roboto, sans-serif" font-size="16" y="18" x="10.684998" stroke-width="0" fill="white">{{number}}</text></g></svg>`
};
function renderTemplate(template, data) {
  return template.replace(/\{\{(\w+)\}\}/g, (_unused, key) => {
    return String(data[key] || "");
  });
}
function renderWarehouseInfobox(props) {
  return renderTemplate(PRERENDERED_INFOBOX_TEMPLATES.warehouse, {
    locationName: props.locationName,
    distance: props.distance,
    measure: props.measure || "mi",
    href: props.href || "#"
  });
}
function renderWarehousePin(props) {
  if ((props == null ? void 0 : props.number) !== void 0) {
    return renderTemplate(PRERENDERED_PIN_TEMPLATES.locator, {
      number: props.number,
      weight: props.weight || 300
    });
  }
  return PRERENDERED_PIN_TEMPLATES.warehouse;
}
function renderLocatorPin(props) {
  return renderTemplate(PRERENDERED_PIN_TEMPLATES.locator, {
    number: props.number,
    weight: props.weight || 300
  });
}
const initializeBingMap = (mapRef, apiKey, midpoint, zoom, mapTypeId, setMap, onMapInitialized) => {
  if (mapRef.current && window.Microsoft) {
    const newMap = new window.Microsoft.Maps.Map(mapRef.current, {
      credentials: apiKey,
      center: new window.Microsoft.Maps.Location(
        midpoint[0],
        midpoint[1]
      ),
      zoom,
      enableHighDpi: true,
      liteMode: true,
      navigationBarMode: window.Microsoft.Maps.NavigationBarMode.minified,
      mapTypeId: mapTypeId || window.Microsoft.Maps.MapTypeId.road,
      showTrafficButton: false
      // Disable traffic layer
      // @ts-expect-error - showTraffic is a valid Bing Maps API option not in TypeScript definitions.
    });
    setMap(newMap);
    if (onMapInitialized) {
      onMapInitialized(newMap);
    }
  }
};
const updateMapCenter = (map, midpoint) => {
  if (map && window.Microsoft) {
    const newLocation = new window.Microsoft.Maps.Location(
      midpoint[0],
      midpoint[1]
    );
    map.setView({ center: newLocation });
  }
};
const fitMapToWarehouses = (map, warehouses, zoom) => {
  if (!map || !window.Microsoft || !warehouses.length) return;
  const microsoft = window.Microsoft;
  if (warehouses.length === 1) {
    const location = new microsoft.Maps.Location(
      warehouses[0].coordinates[0],
      warehouses[0].coordinates[1]
    );
    map.setView({ center: location, zoom });
    return;
  }
  const locations = warehouses.map(
    (w) => new microsoft.Maps.Location(w.coordinates[0], w.coordinates[1])
  );
  const bounds = microsoft.Maps.LocationRect.fromLocations(locations);
  map.setView({ center: map.getCenter(), bounds, padding: 48 });
};
const resetMapToUSA = (map, mapPushpins, setMapPushpins, globalInfobox) => {
  if (!map || !window.Microsoft) return;
  if (mapPushpins && mapPushpins.length > 0) {
    mapPushpins.forEach((pin) => {
      try {
        map.entities.remove(pin);
      } catch {
      }
    });
    setMapPushpins([]);
  }
  if (globalInfobox) {
    try {
      globalInfobox.setOptions({ visible: false });
    } catch {
    }
  }
  try {
    const usa = new window.Microsoft.Maps.Location(37.0902, -95.7129);
    map.setView({ center: usa, zoom: 3 });
  } catch {
  }
};
const createInfobox = (map) => {
  const infobox = new window.Microsoft.Maps.Infobox(map.getCenter(), {
    visible: false,
    offset: new window.Microsoft.Maps.Point(-120, 45)
  });
  infobox.setMap(map);
  return infobox;
};
const createCloseInfoboxHandler = (setCurrentInfobox, mapPushpinsRef) => {
  return () => {
    mapPushpinsRef.current.forEach((pin) => {
      var _a;
      if ((_a = pin.metadata) == null ? void 0 : _a.infobox) {
        pin.metadata.infobox.setOptions({
          visible: false
        });
      }
    });
    setCurrentInfobox(null);
  };
};
const updateMapPushpins = (map, mapPushpins, setMapPushpins, template, warehouses, eventHandler, setCurrentInfobox, globalInfobox) => {
  if (!map || !window.Microsoft || !warehouses.length) {
    return;
  }
  const microsoft = window.Microsoft;
  if (mapPushpins.length > 0) {
    mapPushpins.forEach((pin) => map.entities.remove(pin));
  }
  const iconTemplate = template === "tmpl_warehouse_pin" ? renderWarehousePin() : null;
  const shouldAddEventHandlers = warehouses.length > 1;
  const newPushpins = warehouses.map((warehouse, index) => {
    var _a;
    const location = new microsoft.Maps.Location(
      warehouse.coordinates[0],
      warehouse.coordinates[1]
    );
    const icon = iconTemplate || renderLocatorPin({
      number: index + 1,
      weight: 300
    });
    const pin = new window.Microsoft.Maps.Pushpin(location, {
      icon,
      anchor: new window.Microsoft.Maps.Point(10.5185, 30)
    });
    const href = generateWarehouseHref(
      warehouse.name,
      warehouse.city,
      warehouse.state,
      warehouse.warehouseId
    );
    pin.metadata = {
      infobox: globalInfobox,
      location,
      index: index + 1,
      warehouseId: warehouse.warehouseId,
      warehouseName: warehouse.name,
      coordinates: warehouse.coordinates,
      city: warehouse.city,
      state: warehouse.state,
      distance: warehouse.distance,
      distanceUnit: warehouse.distanceUnit,
      href
    };
    if (shouldAddEventHandlers) {
      (_a = window.Microsoft) == null ? void 0 : _a.Maps.Events.addHandler(
        pin,
        "click",
        (e) => {
          const clickedPin = e.target || pin;
          const metadata = clickedPin.metadata;
          if (eventHandler) {
            eventHandler({ ...e, pinMetadata: metadata });
          }
          if (globalInfobox && metadata) {
            globalInfobox.setOptions({
              location: metadata.location,
              htmlContent: renderWarehouseInfobox({
                locationName: metadata.warehouseName || "Unknown",
                distance: (metadata.distance || 0).toFixed(2),
                measure: metadata.distanceUnit || "mi",
                href: metadata.href || "#"
              }),
              visible: true
            });
            if (setCurrentInfobox) {
              setCurrentInfobox(globalInfobox);
            }
          }
        }
      );
    }
    return pin;
  });
  setMapPushpins(newPushpins);
  newPushpins.forEach((pin) => map.entities.push(pin));
};
export {
  PRERENDERED_INFOBOX_TEMPLATES,
  PRERENDERED_PIN_TEMPLATES,
  computeMapData,
  computeMapDataWithWarehouses,
  createCloseInfoboxHandler,
  createInfobox,
  fitMapToWarehouses,
  generateWarehouseHref,
  getMidpoint,
  initializeBingMap,
  renderLocatorPin,
  renderTemplate,
  renderWarehouseInfobox,
  renderWarehousePin,
  resetMapToUSA,
  selectPin,
  updateMapCenter,
  updateMapPushpins
};
